/**
 * Feedback Page
 * 
 * Thank you page for early readers with links to feedback forms.
 * Features the exact content provided by the user with proper formatting.
 */

import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Heart, BookOpen, Star, MessageCircle, Users, GraduationCap } from "lucide-react";

export default function Feedback() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 to-forest-50">
      {/* Main Content */}
      <section className="py-20 lg:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <Badge className="mb-6 bg-forest-100 text-forest-700 border-forest-200 text-lg px-4 py-2">
              🌱 Thank You, Early Readers!
            </Badge>
            <div className="w-20 h-20 bg-forest-600 rounded-full flex items-center justify-center mx-auto mb-8">
              <Heart className="w-10 h-10 text-white" />
            </div>
          </motion.div>

          {/* Main Thank You Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="border-2 border-forest-200 bg-white/80 backdrop-blur-sm shadow-xl">
              <CardContent className="p-12">
                <div className="text-forest-700 space-y-6 text-lg leading-relaxed">
                  <p className="text-2xl font-medium text-forest-800 mb-8">
                    Thank you so much for being among the very first readers of 
                    "Where the Brave Things Grow." Your willingness to review this book 
                    and share your thoughts means the world to us.
                  </p>
                  
                  <p>
                    "Where the Brave Things Grow" is a heartfelt homage to the classic 
                    children's book, "Where the Wild Things Are." In Maurice Sendak's 
                    story, Max travels to a land of wild things, but ultimately returns 
                    home—because it's in coming back, not staying in the wild, that he 
                    finds comfort and growth.
                  </p>
                  
                  <p>
                    This book builds on this idea by inviting readers to embark on their 
                    own journey of emotional growth. "Where the Brave Things Grow" gives 
                    children and adults space to experience and understand their feelings 
                    on their own terms. It shows that true bravery isn't about taming 
                    wildness, but about having the courage to engage with our emotions 
                    and grow from that experience.
                  </p>
                  
                  <p>
                    Thank you for helping this book—and its readers—grow braver, 
                    kinder, and more connected!
                  </p>
                  
                  <div className="text-right mt-8">
                    <p className="font-medium text-forest-800">
                      With gratitude,<br />
                      Where the Brave Things Grow Team
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Feedback Links Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-16 text-center"
          >
            <h2 className="text-3xl font-bold text-forest-800 mb-8">
              Click below to share your valuable feedback!
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {/* Parent/Caregiver Feedback */}
              <Card className="border-2 border-coral-200 shadow-xl transition-all duration-300 hover:shadow-2xl group flex flex-col overflow-hidden">
                <CardContent className="p-8 text-center flex flex-col h-full">
                  <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-coral-200 transition-colors">
                    <Users className="w-8 h-8 text-coral-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Parent/Caregiver
                  </h3>
                  <p className="text-gray-600 mb-6 flex-grow">
                    Share your experience reading with your child and how the book supported your family's conversations about emotions.
                  </p>
                  <Button 
                    className="w-full bg-coral-600 hover:bg-coral-700 text-white px-6 py-4 text-base h-auto whitespace-normal"
                    onClick={() => window.open('https://forms.clickup.com/2370929/f/28bbh-75171/DGU9D6EC4XJIQE26DB', '_blank')}
                  >
                    <MessageCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                    <span>Share Parent Feedback</span>
                  </Button>
                </CardContent>
              </Card>

              {/* Educator Feedback */}
              <Card className="border-2 border-golden-200 shadow-xl transition-all duration-300 hover:shadow-2xl group flex flex-col overflow-hidden">
                <CardContent className="p-8 text-center flex flex-col h-full">
                  <div className="w-16 h-16 bg-golden-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-golden-200 transition-colors">
                    <GraduationCap className="w-8 h-8 text-golden-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Educator
                  </h3>
                  <p className="text-gray-600 mb-6 flex-grow">
                    Share how the book worked in your classroom or educational setting and its impact on social-emotional learning.
                  </p>
                  <Button 
                    className="w-full bg-golden-600 hover:bg-golden-700 text-white px-6 py-4 text-base h-auto whitespace-normal"
                    onClick={() => window.open('https://forms.clickup.com/2370929/f/28bbh-78551/46A6ZVX9MQTI5KWDOK', '_blank')}
                  >
                    <Star className="w-5 h-5 mr-2 flex-shrink-0" />
                    <span>Share Educator Feedback</span>
                  </Button>
                </CardContent>
              </Card>

              {/* General Feedback */}
              <Card className="border-2 border-forest-200 shadow-xl transition-all duration-300 hover:shadow-2xl group flex flex-col overflow-hidden">
                <CardContent className="p-8 text-center flex flex-col h-full">
                  <div className="w-16 h-16 bg-forest-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-forest-200 transition-colors">
                    <MessageCircle className="w-8 h-8 text-forest-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    General Reader
                  </h3>
                  <p className="text-gray-600 mb-6 flex-grow">
                    Share your thoughts and experience with the book, regardless of your background or setting.
                  </p>
                  <Button 
                    className="w-full bg-forest-600 hover:bg-forest-700 text-white px-6 py-4 text-base h-auto whitespace-normal"
                    onClick={() => window.open('https://forms.clickup.com/2370929/f/28bbh-78731/WYK4979Z4L1R5DM5M1', '_blank')}
                  >
                    <BookOpen className="w-5 h-5 mr-2 flex-shrink-0" />
                    <span>Share General Feedback</span>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Additional Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-16 text-center"
          >
            <Card className="border-2 border-forest-200 bg-forest-50">
              <CardContent className="p-8">
                <BookOpen className="w-12 h-12 text-forest-600 mx-auto mb-4" />
                <p className="text-forest-700 text-lg">
                  Your feedback helps us continue creating meaningful stories that support 
                  children's emotional growth and family connections.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
